import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { Validators, FormGroup, NonNullableFormBuilder } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Subject, noop } from 'rxjs';
import { filter, map, takeUntil } from 'rxjs/operators';
import { GroupListItemResponse, UserItemResponse, UserResponse } from '../../../../../../api';
import { BaseRolesService } from '../../../../../core/services/base-roles.service';
import { BaseAvatarSize } from '../../../../../shared/components/avatar/base-avatar.component';
import { BaseButtonSize } from '../../../../../shared/components/button/base-button.component';
import { BaseGenderOptions } from '../../../../../shared/constants/base-gender-options';
import { BaseModalService } from '../../../../../shared/services/base-modal.service';
import { baseAdministrationActions, baseAdministrationSelectors, BaseCoreState } from '../../../../../_store';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { basePasswordValidator } from '../../../../../shared/validators/base-password.validator';
import { baseMatchValuesValidator } from '../../../../../shared/validators/base-match-values.validator';
import {
  EditUserFormInterface,
  BaseChangePasswordFormInterface,
  UserCustomerFormInterface,
  UserRoles,
} from '../../../_interfaces/base-user-management.interface';
import { BASE_ROLES } from '../../../../../core/constants/base-roles';
import { AUTHORITY_ROLES } from '../../../_constans/user-authority-roles.const';
import { BaseGender } from '../../../../../shared/enums/base-gender.enum';
import { GenderType } from '../../../_models/base-user-profile.type';
import { BaseNameIdInterface } from '../../../../../shared/interfaces/base-name-id.interface';
import { BaseUserManagementService } from '../../../_services/base-user-management.service';

@Component({
  selector: 'base-edit-user-form',
  templateUrl: './base-edit-user-form.component.html',
})
export class BaseEditUserFormComponent implements OnInit, OnDestroy {
  @ViewChild('changePasswordTemplate', { static: true }) changePasswordTemplate: ElementRef;
  @Input() form: FormGroup<EditUserFormInterface>;
  @Input() user: UserItemResponse;
  @Input() isFormSubmitted: boolean;
  @Input() groups: GroupListItemResponse[] = [];
  @Output() editUser: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() cancelUserEdit: EventEmitter<boolean> = new EventEmitter<boolean>();

  modalRef: NgbModalRef;
  userData: UserResponse | undefined;
  permissionGroupValue: GroupListItemResponse | undefined;
  isChangePasswordFormSubmitted = false;
  avatarSizes = BaseAvatarSize;
  buttonSizes = BaseButtonSize;
  genderOptions = BaseGenderOptions;
  isAdmin = false;
  isWorkCouncil = false;
  isPsp = false;
  changePasswordForm: FormGroup<BaseChangePasswordFormInterface>;
  baseRoles = BASE_ROLES;
  locations: BaseNameIdInterface[] = [];

  private destroy$ = new Subject<void>();

  constructor(
    private store: Store<BaseCoreState>,
    private modalService: BaseModalService,
    private fb: NonNullableFormBuilder,
    private rolesService: BaseRolesService,
    private userManagementService: BaseUserManagementService
  ) {}

  get hasGroupControl(): boolean {
    return this.form.contains('group');
  }

  get hasCustomerLocationControl(): boolean {
    return this.form.contains('customerLocations');
  }

  ngOnInit(): void {
    this.isAdmin = this.rolesService.hasAdminRole();
    this.isWorkCouncil = this.rolesService.hasWorkCouncilRole();
    this.isPsp = this.rolesService.isPsp();

    this.initChangePasswordForm();
    this.handleUserProfileData();

    if (!this.isPsp) {
      this.getCustomerLocations();
      this.customerFormRoleControlHandler(this.form as unknown as FormGroup<UserCustomerFormInterface>);
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();

    if (!this.isPsp) {
      this.userManagementService.removeOptionalFormControls(
        this.form as unknown as FormGroup<UserCustomerFormInterface>
      );
      this.store.dispatch(baseAdministrationActions.resetLocations());
    }
  }

  confirmEditUser(): void {
    this.editUser.emit();
  }

  cancelEditUser(): void {
    this.cancelUserEdit.emit();
  }

  openChangePasswordModal(): void {
    this.isChangePasswordFormSubmitted = false;
    this.changePasswordForm.reset({
      newPassword: '',
      newPasswordConfirm: '',
    });
    this.changePasswordForm.controls.newPassword.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.changePasswordForm.controls.newPasswordConfirm.setValue(
        this.changePasswordForm.controls.newPasswordConfirm.value
      );
    });
    this.modalRef = this.modalService.open(
      {
        headText: 'auth.password.changeTitle',
        bodyTemplate: this.changePasswordTemplate,
        okButtonDisplayed: false,
        cancelButtonDisplayed: false,
        hideFooter: true,
      },
      {
        backdrop: 'static',
        size: 'lg',
      }
    );

    this.modalRef.result.then(noop, noop);
  }

  changeUserPassword(): void {
    this.isChangePasswordFormSubmitted = true;
    if (this.changePasswordForm.valid) {
      this.store.dispatch(
        baseAdministrationActions.updateUserPassword({
          payload: {
            user: this.user.party,
            body: { newPassword: this.changePasswordForm.get('newPassword')?.getRawValue() },
          },
        })
      );
      this.store
        .select(baseAdministrationSelectors.getUserPasswordUpdateStatus)
        .pipe(
          takeUntil(this.destroy$),
          filter((profilePasswordStatus) => !!profilePasswordStatus)
        )
        .subscribe(() => {
          this.modalRef.close();
        });
    }
  }

  cancelChangeUserPassword(): void {
    this.modalRef.close();
  }

  handleUserProfileData() {
    this.store
      .select(baseAdministrationSelectors.getUserProfile)
      .pipe(
        takeUntil(this.destroy$),
        filter((state) => !!state.ok),
        map((state) => state.data as UserResponse)
      )
      .subscribe((user) => {
        this.userData = user;
        this.permissionGroupValue = this.groups.find((group) => group.id === user.group?.id);
        this.form.patchValue({
          ...user,
          gender: { label: BaseGender[user.gender || 1], value: (user.gender || 1) as GenderType },
          role: this.getActiveRole(user),
          customerLocations: (user.workCouncilCustomerLocations || []).map((loc) => ({
            id: loc.id,
            name: loc.name,
          })),
        });

        this.setupInitialControls(user);
      });
  }

  customerFormRoleControlHandler(customerForm: FormGroup<UserCustomerFormInterface>) {
    this.form.controls.role.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((role) => {
      switch (role) {
        case BASE_ROLES.CUSTOMER_WORK_COUNCIL:
          this.userManagementService.addControls(customerForm, ['customerLocations']);
          this.userManagementService.removeControls(customerForm, ['group']);
          break;
        case BASE_ROLES.CUSTOMER_USER:
          this.userManagementService.addControls(customerForm, ['group']);
          this.userManagementService.removeControls(customerForm, ['customerLocations']);
          break;
        case BASE_ROLES.CUSTOMER_ADMIN:
          this.userManagementService.removeOptionalFormControls(customerForm);
      }
    });
  }

  initChangePasswordForm() {
    this.changePasswordForm = this.fb.group({
      newPassword: ['', [Validators.required, basePasswordValidator(), Validators.maxLength(30)]],
      newPasswordConfirm: this.fb.control('', {
        validators: Validators.compose([Validators.required, baseMatchValuesValidator('newPassword')]),
      }),
    });
  }

  private getCustomerLocations() {
    this.userManagementService
      .getCustomerLocations()
      .pipe(takeUntil(this.destroy$))
      .subscribe((locations) => (this.locations = locations));
  }

  private setupInitialControls(user: UserResponse) {
    if (user.roles.includes(BASE_ROLES.CUSTOMER_WORK_COUNCIL)) {
      this.userManagementService.addControls(this.form, ['customerLocations']);
      this.userManagementService.removeControls(this.form, ['group']);
    } else if (user.roles.includes(BASE_ROLES.CUSTOMER_ADMIN)) {
      this.userManagementService.removeOptionalFormControls(
        this.form as unknown as FormGroup<UserCustomerFormInterface>
      );
    } else {
      this.userManagementService.addControls(this.form, ['group']);
      this.userManagementService.removeControls(this.form, ['customerLocations']);

      this.form.controls.group!.setValue(user.group);
    }
  }

  private getActiveRole(user: UserResponse): UserRoles {
    const authority = AUTHORITY_ROLES.find((role) => user.roles?.includes(role));
    return (authority ?? (this.isPsp ? BASE_ROLES.PSP_USER : BASE_ROLES.CUSTOMER_USER)) as UserRoles;
  }
}
